import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint, debounce } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /**
   * Share of the drawer the summary may take before it stops being pinned.
   *
   * The reasoning behind unpinning at all: a sticky summary holds its space
   * permanently, so past a certain size it leaves only a sliver for the items
   * to scroll in. Letting it scroll with everything else gives the list the
   * full height back while the customer is reading it.
   *
   * Raised from the stock 0.5 because this summary carries more than a plain
   * Horizon one — savings banner, trust columns, payment badges — and was
   * tripping the guard on ordinary laptop screens. Kept below 1 so the guard
   * still fires on short viewports, where a summary filling almost the whole
   * drawer really would be unusable.
   *
   * @type {number}
   */
  #summaryThreshold = 0.7;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#restartOpenAnimation);
    this.addEventListener(DialogOpenEvent.eventName, this.#scheduleStickyUpdate);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    window.addEventListener('resize', this.#handleStickyResize);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#restartOpenAnimation);
    this.removeEventListener(DialogOpenEvent.eventName, this.#scheduleStickyUpdate);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    window.removeEventListener('resize', this.#handleStickyResize);
    this.#historyAbortController?.abort();
  }

  /**
   * Restarts the drawer's opening keyframes.
   *
   * Both directions are plain CSS animations declared on `.dialog-drawer`
   * (`move-and-fade` in base.css); only the closing one is explicitly
   * restarted, by `closeDialog` in dialog.js. WebKit does not reliably replay
   * the opening keyframes when the dialog is promoted to the top layer, so the
   * drawer appears with no animation at all while the closing one still runs.
   * This mirrors what `closeDialog` already does, scoped to the cart drawer so
   * the shared dialog component is left untouched.
   *
   * Runs on DialogOpenEvent, which is dispatched in the same frame as
   * `showModal()`, so the restart happens before the first painted frame.
   */
  #restartOpenAnimation = () => {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    dialog.style.animation = 'none';
    // Forced reflow: drops the cancelled animation before the stylesheet one is restored.
    void dialog.offsetWidth;
    dialog.style.animation = '';
  };

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);

    // `CartAddEvent.eventName` is `cart:update`, so this also covers quantity
    // changes and removals: the summary's height moves with them.
    this.#scheduleStickyUpdate();
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  /**
   * Defers the measurement by two frames.
   *
   * DialogOpenEvent fires in the same frame as `showModal()`, before WebKit has
   * laid out the dialog it has just promoted to the top layer. Measuring there
   * returned a height of 0, the ratio below came out as Infinity, and the
   * summary was pinned unsticky for the whole session.
   */
  #scheduleStickyUpdate = () => {
    requestAnimationFrame(() => requestAnimationFrame(this.#updateStickyState));
  };

  #handleStickyResize = debounce(() => {
    if (this.refs.dialog?.open) this.#scheduleStickyUpdate();
  }, 100);

  #updateStickyState = () => {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog?.open) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;

    // A measurement of zero means the layout is not settled yet. Deciding on it
    // would divide by zero and latch the attribute to "false"; leaving the
    // current value alone is always the safer answer.
    if (drawerHeight <= 0 || summaryHeight <= 0) return;

    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  };
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
